import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LifeBuoy, X, Paperclip, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import ErrorAlert from './ErrorAlert.jsx'
import useMe from '../hooks/useMe.js'
import { BTN_PRIMARY } from '../lib/ui.js'
import {
  MAX_DESCRIPTION,
  MAX_FILES,
  MAX_TOTAL_BYTES,
  validateAddFiles,
  filesFromClipboard,
  buildSessionContext,
  submitReport,
} from '../lib/selfReport.js'

function formatBytes(n) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export default function HelpPanel({ open, onClose }) {
  const { me } = useMe()
  const location = useLocation()
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState([])
  const [showContext, setShowContext] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [serverHealth, setServerHealth] = useState(null)
  const fileInputRef = useRef(null)

  const route = `${location.pathname}${location.search || ''}`
  const context = useMemo(
    () => buildSessionContext({ me, route, serverHealth }),
    // recompute when the panel opens so recentErrors snapshot is fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, route, serverHealth, open],
  )
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    fetch('/api/v1/health', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) setServerHealth(j) })
      .catch(() => { if (!cancelled) setServerHealth(null) })
    return () => { cancelled = true }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const addFiles = (incoming) => {
    setSubmitError(null)
    const result = validateAddFiles(files, incoming)
    if (!result.ok) {
      setSubmitError(result.error)
      return
    }
    setFiles(result.files)
  }

  const onPaste = (e) => {
    const pasted = filesFromClipboard(e.clipboardData)
    if (pasted.length) {
      e.preventDefault()
      addFiles(pasted)
    }
  }

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx))

  const reset = () => {
    setDescription('')
    setFiles([])
    setSubmitError(null)
    setSuccess(null)
    setShowContext(false)
  }

  const onSubmit = async (e) => {
    e?.preventDefault?.()
    if (!description.trim()) return
    setSubmitting(true)
    setSubmitError(null)
    const result = await submitReport({ description, files, context })
    setSubmitting(false)
    if (result.ok) {
      setSuccess({ issueUrl: result.issueUrl, reportId: result.reportId })
    } else {
      setSubmitError(result.error)
    }
  }

  if (!open) return null

  return (
    <div role="dialog" aria-modal="true" aria-label="Report a problem" className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <aside
        className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
        onPaste={onPaste}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-brand-600" aria-hidden="true" />
            <h2 className="text-base font-semibold text-slate-900">Report a problem</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {success ? (
            <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <div className="font-semibold">Thanks — report submitted.</div>
              {success.issueUrl ? (
                <div>
                  Tracking issue:{' '}
                  <a href={success.issueUrl} target="_blank" rel="noreferrer" className="font-medium underline">
                    {success.issueUrl}
                  </a>
                </div>
              ) : null}
              {success.reportId ? (
                <div className="font-mono text-xs">Report ID: {success.reportId}</div>
              ) : null}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={reset}
                  className="rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-emerald-800 hover:bg-emerald-100"
                >
                  Submit another
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-white hover:bg-emerald-700"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              {submitError ? <ErrorAlert title={submitError.title} message={submitError.message} /> : null}
              <div>
                <label htmlFor="self-report-description" className="block text-sm font-medium text-slate-700">
                  What happened?
                </label>
                <textarea
                  id="self-report-description"
                  rows={6}
                  required
                  maxLength={MAX_DESCRIPTION}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what you were doing and what went wrong. Paste screenshots with Cmd/Ctrl+V."
                  className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <div className="mt-1 text-xs text-slate-500">
                  {description.length} / {MAX_DESCRIPTION} characters
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700">Attachments</label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                    Add file
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/*,application/pdf,text/plain"
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files || []))
                    e.target.value = ''
                  }}
                />
                {files.length === 0 ? (
                  <p className="mt-2 text-xs text-slate-500">
                    Optional. Up to {MAX_FILES} files, {formatBytes(MAX_TOTAL_BYTES)} total. Images, PDF, plain text.
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                        <span className="truncate text-slate-700">{f.name} <span className="text-slate-400">({formatBytes(f.size)})</span></span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          aria-label={`Remove ${f.name}`}
                          className="ml-2 inline-flex items-center text-slate-400 hover:text-rose-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                    <li className="text-xs text-slate-500">
                      {files.length} / {MAX_FILES} files · {formatBytes(totalBytes)} / {formatBytes(MAX_TOTAL_BYTES)}
                    </li>
                  </ul>
                )}
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setShowContext((v) => !v)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-slate-700"
                  aria-expanded={showContext}
                >
                  <span>What we send</span>
                  {showContext ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                </button>
                {showContext ? (
                  <pre className="max-h-64 overflow-auto border-t border-slate-200 bg-white p-3 text-[11px] leading-snug text-slate-700">
                    {JSON.stringify(context, null, 2)}
                  </pre>
                ) : null}
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!description.trim() || submitting}
                  className={BTN_PRIMARY}
                >
                  {submitting ? 'Sending…' : 'Submit report'}
                </button>
              </div>
            </form>
          )}
        </div>
      </aside>
    </div>
  )
}
